import { useMemo, useState } from 'react'

import { HistoricalPricesRangePreset } from '@/common/types'
import { useQueries } from '@tanstack/react-query'
import { API_ROUTES } from '@/common/constants'
import { getHistoricalPrice } from '@/trading/tradingApi'
import { cn } from '@/common/utils/utils'
import { aggregateHistorical, type SecurityUnit } from './bundleUtils'
import { Button, H2 } from '@/common/ui'
import { analytics } from '@/common/analytics/analytics'
import { HistoricalPriceChart } from '@/securities/historicalPriceChart'
import { BUNDLE_COPY } from '@/locales/en'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'

const rangePresets: { range: HistoricalPricesRangePreset; label: string; showOnMobile?: boolean }[] = [
    { range: '1Y', label: '1Y' },
    { range: '3Y', label: '3Y' },
    { range: '5Y', label: '5Y' },
]

export const BundleGraph = ({ securityList }: { securityList: SecurityUnit[] }) => {
    const [selectedRange, setSelectedRange] = useState<HistoricalPricesRangePreset>('1Y')
    // show loader while there are no securities to display

    const historicalResults = useQueries({
        queries: securityList.map((s) => ({
            queryKey: [API_ROUTES.historicalPrices, s.securityCode, selectedRange],
            queryFn: () => getHistoricalPrice(s.securityCode, selectedRange),
            enabled: !!s.securityCode,
        })),
    })

    const aggregatedPricePoints = useMemo(() => {
        if (securityList.length === 0) return []
        if (!historicalResults.every((q) => !q?.isLoading && q?.data !== undefined)) return []
        return aggregateHistorical(securityList, historicalResults)
    }, [securityList, historicalResults])

    const isAnyLoading = historicalResults.some((q) => q?.isLoading)

    const handlePresetClick = (range: HistoricalPricesRangePreset) => {
        setSelectedRange(range)
        analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_CHART_RANGE_CLICKED, {
            preset_range: range,
        })
    }

    return (
        <div className="w-full gap-6 flex flex-col justify-start items-start">
            <H2 className="text-center justify-start text-black text-3xl  md:text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                {BUNDLE_COPY.chartTitle}
            </H2>

            <div className="flex flex-row items-end">
                <ul className="w-full flex overflow-x-auto justify-start space-x-2 pr-4 mt-2">
                    {rangePresets.map((preset) => (
                        <li key={preset.range} className={`block`}>
                            <Button
                                onClick={() => handlePresetClick(preset.range)}
                                className={cn('items-center h-auto w-10 leading-[10px] mb-1 font-normal rounded-3xl', {
                                    'bg-secondary-light': selectedRange === preset.range,
                                })}
                                size="sm"
                                variant="tertiary"
                            >
                                {preset.label}
                            </Button>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="w-full flex flex-col items-start gap-4">
                <HistoricalPriceChart
                    isLoading={isAnyLoading}
                    rangePreset={selectedRange}
                    data={aggregatedPricePoints}
                />
                <div className="justify-center text-content-tertiary text-xs font-normal font-['Inter'] leading-5">
                    {BUNDLE_COPY.chartDisclaimer}
                </div>
            </div>
        </div>
    )
}
